import type { ProjectTask } from "@/types/models";

export type ScheduleHealthLevel = "On track" | "At risk" | "Off track";

export interface ScheduleHealthResult {
  level: ScheduleHealthLevel;
  reason: string;
  overdueCount: number;
  blockedCount: number;
  beyondTargetCount: number;
}

const finishedStatuses = new Set(["Complete", "Not applicable", "Cancelled"]);

export function calculateScheduleHealth(input: {
  tasks: readonly ProjectTask[];
  today: string;
  projectTargetEndDate: string;
}): ScheduleHealthResult {
  const active = input.tasks.filter(
    (task) => task.type !== "summary" && !finishedStatuses.has(task.status),
  );
  const overdueCount = active.filter(
    (task) => task.dueDate && task.dueDate < input.today,
  ).length;
  const blockedCount = active.filter(
    (task) => task.status === "Blocked",
  ).length;
  const beyondTargetCount = active.filter(
    (task) =>
      task.dueDate &&
      input.projectTargetEndDate &&
      task.dueDate > input.projectTargetEndDate,
  ).length;

  if (overdueCount || beyondTargetCount) {
    const reasons = [
      overdueCount
        ? `${overdueCount} incomplete ${overdueCount === 1 ? "item is" : "items are"} overdue`
        : "",
      beyondTargetCount
        ? `${beyondTargetCount} incomplete ${beyondTargetCount === 1 ? "item finishes" : "items finish"} after the project target`
        : "",
    ].filter(Boolean);
    return {
      level: "Off track",
      reason: `${reasons.join(" and ")}. Review dates, dependencies, and ownership before relying on the target.`,
      overdueCount,
      blockedCount,
      beyondTargetCount,
    };
  }

  if (blockedCount) {
    return {
      level: "At risk",
      reason: `${blockedCount} incomplete ${blockedCount === 1 ? "item is" : "items are"} blocked. Resolve the blocker to protect the current schedule.`,
      overdueCount,
      blockedCount,
      beyondTargetCount,
    };
  }

  return {
    level: "On track",
    reason: active.length
      ? "No visible incomplete work is overdue, blocked, or scheduled beyond the project target."
      : "All visible work is complete or no longer applies.",
    overdueCount,
    blockedCount,
    beyondTargetCount,
  };
}
